// Pre-filled numbers for the store and kitchen forms (kitchen flow, lane C).
// The happy path is one tap: every box already holds the number that is most
// likely right, and only a change downwards asks why.
import type { RequisitionLine } from './types'
import { T } from './strings'
import { formatDateHuman } from '@/lib/utils'
import { fmtQty } from '@/lib/quantity'

const EPS = 1e-6

/**
 * What the store can send: its balance less lots already past their expiry
 * (pilot fix A2). Expired stock is never offered or pre-counted.
 */
export function storeUsable(l: RequisitionLine): number {
  return l.source_usable_qty ?? l.source_on_hand
}

/** Issue: what is still owed, capped at what the store holds in date. */
export function issuePrefill(l: RequisitionLine): number {
  const owed = Math.max(l.to_issue_qty, 0)
  return round3(Math.min(owed, Math.max(storeUsable(l), 0)))
}

/** "2 kg expired on Wed 7 Oct — throw it away", or null when nothing has expired. */
export function expiredWarning(l: RequisitionLine): string | null {
  const q = l.source_expired_qty ?? 0
  if (q <= EPS || !l.source_expired_first) return null
  const from = formatDateHuman(l.source_expired_first)
  const to = l.source_expired_last ? formatDateHuman(l.source_expired_last) : from
  return from === to
    ? T.expiredThrowAway(fmtQty(q), l.unit, from)
    : T.expiredThrowAwayRange(fmtQty(q), l.unit, from, to)
}

/** Receive: everything that was sent and not yet checked. */
export function receivePrefill(l: RequisitionLine): number {
  return round3(Math.max(l.awaiting_receipt_qty, 0))
}

/** Approve: what was asked for. */
export function approvePrefill(l: RequisitionLine): number {
  return round3(l.requested_qty)
}

/** Less arrived than was sent → the backend requires a reason. */
export function receiveNeedsReason(l: RequisitionLine, got: number): boolean {
  return got < l.awaiting_receipt_qty - EPS
}

/** Approving less than asked → the backend requires a reason. */
export function approveNeedsReason(l: RequisitionLine, approved: number): boolean {
  return approved < l.requested_qty - EPS
}

export function receiveError(l: RequisitionLine, got: number): string | null {
  if (!Number.isFinite(got) || got < 0) return 'Enter a number'
  if (got > l.in_transit_qty + EPS) return `Only ${round3(l.in_transit_qty)} ${l.unit} was sent`
  return null
}

export function issueError(l: RequisitionLine, qty: number): string | null {
  if (!Number.isFinite(qty) || qty < 0) return 'Enter a number'
  if (qty > l.to_issue_qty + EPS) return `Only ${round3(l.to_issue_qty)} ${l.unit} is still to send`
  if (qty > storeUsable(l) + EPS) {
    return (l.source_expired_qty ?? 0) > EPS
      ? T.storeHasInDate(String(round3(Math.max(storeUsable(l), 0))), l.unit)
      : `The store has only ${round3(l.source_on_hand)} ${l.unit}`
  }
  return null
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000
}

/**
 * Which receive form a requisition needs:
 *   'arrivals' — something was sent and not yet checked (the normal case);
 *   'found'    — nothing is on its way, but stock reported missing is still in
 *                transit and may turn up later;
 *   'none'     — nothing to receive.
 * Showing the arrivals form in the 'found' case put a "Sent 0 kg · Missing 1"
 * box with a 0 in it on screen, which read as unfinished work and errored if
 * saved.
 */
export type ReceiveMode = 'arrivals' | 'found' | 'none'

export function receiveMode(lines: RequisitionLine[]): ReceiveMode {
  if (lines.some((l) => l.awaiting_receipt_qty > EPS)) return 'arrivals'
  if (lines.some((l) => l.short_received_qty > EPS && l.in_transit_qty > EPS)) return 'found'
  return 'none'
}

export function receiveLinesFor(mode: ReceiveMode, lines: RequisitionLine[]): RequisitionLine[] {
  if (mode === 'arrivals') return lines.filter((l) => l.awaiting_receipt_qty > EPS)
  if (mode === 'found') return lines.filter((l) => l.short_received_qty > EPS && l.in_transit_qty > EPS)
  return []
}

/** Pre-fill: arrivals start at what was sent; found goods start at 0. */
export function receiveStart(mode: ReceiveMode, l: RequisitionLine): number {
  return mode === 'found' ? 0 : receivePrefill(l)
}

/**
 * What happened to the part that did not arrive (Batch 5 lane tablet):
 * 'coming' — the rest is on a later trip, it stays on its way (no reason);
 * 'missing' — it is recorded missing and needs a reason; undefined — not
 * chosen yet.
 */
export type ShortChoice = 'coming' | 'missing'

/** Can the form be saved? Found-goods needs at least one non-zero line;
 *  arrivals need a choice (and, for missing, a reason) on every short line,
 *  and at least one line with something in it or a full receipt. */
export function receiveReady(
  mode: ReceiveMode,
  lines: RequisitionLine[],
  got: Record<string, number>,
  why: Record<string, string>,
  choice: Record<string, ShortChoice | undefined> = {},
): boolean {
  if (lines.length === 0) return false
  for (const l of lines) {
    const v = got[l.id] ?? 0
    if (receiveError(l, v)) return false
    if (!receiveNeedsReason(l, v)) continue
    if (mode === 'arrivals' && choice[l.id] === 'coming') continue
    // A reason typed with no choice made counts as "missing", as before.
    if (!(why[l.id] ?? '').trim()) return false
  }
  if (mode === 'found') return lines.some((l) => (got[l.id] ?? 0) > EPS)
  // Every line "nothing yet, still coming": there is nothing to save.
  return lines.some((l) => !(receiveNeedsReason(l, got[l.id] ?? 0) && choice[l.id] === 'coming') || (got[l.id] ?? 0) > EPS)
}

/** The receive body: each line with what arrived, the reason, and rest_coming. */
export function receiveBody(
  mode: ReceiveMode,
  lines: RequisitionLine[],
  got: Record<string, number>,
  why: Record<string, string>,
  choice: Record<string, ShortChoice | undefined> = {},
): { line_id: string; received_qty: number; reason: string; rest_coming?: boolean }[] {
  return lines
    .filter((l) => mode === 'arrivals' || (got[l.id] ?? 0) > 0)
    .map((l) => {
      const v = got[l.id] ?? 0
      const coming = mode === 'arrivals' && receiveNeedsReason(l, v) && choice[l.id] === 'coming'
      return coming
        ? { line_id: l.id, received_qty: v, reason: '', rest_coming: true }
        : { line_id: l.id, received_qty: v, reason: why[l.id] ?? '' }
    })
}
